import type { TenantInfo } from "@scoach/types";
import { Alert } from "@scoach/ui/icons";
import { type FormEvent, useEffect, useState } from "react";
import { buildTenantUrl, getTenantSubdomain } from "../../../lib/subdomain.ts";
import { isSuperAdminEmail } from "../../../lib/admins.ts";
import { companyApi } from "../../company/api.ts";


import {
  isAllowedEmail,
  signInWithGoogle,
  signOut,
} from "../../../lib/firebase.ts";
import { PrivacyPolicyModal } from "../../dashboard/components/PrivacyPolicyModal.tsx";
import { TermsOfUseModal } from "../../dashboard/components/TermsOfUseModal.tsx";
import { useAuthStore } from "../store.ts";

export interface SignInScreenProps {
  onSignedIn?: () => void;
}

export function SignInScreen({ onSignedIn }: SignInScreenProps) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const currentSubdomain = getTenantSubdomain();
  const [tenantInfo, setTenantInfo] = useState<TenantInfo | null>(null);
  const [_tenantLoading, setTenantLoading] = useState(Boolean(currentSubdomain));
  const [subdomainInput, setSubdomainInput] = useState("");
  const [showWorkspaceFinder, setShowWorkspaceFinder] = useState(false);

  useEffect(() => {
    if (!currentSubdomain) return;
    let active = true;
    setTenantLoading(true);
    companyApi
      .resolveSubdomain(currentSubdomain)
      .then((info) => {
        if (active && info) {
          setTenantInfo(info);
          if (info.domain && !email) {
            setEmail(`@${info.domain}`);
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        if (active) setTenantLoading(false);
      });
    return () => {
      active = false;
    };
  }, [currentSubdomain]);

  function handleGoToWorkspace(e: FormEvent) {
    e.preventDefault();
    const clean = subdomainInput.trim().toLowerCase().replace(/[^a-z0-9-]/g, "");
    if (!clean) return;
    window.location.href = buildTenantUrl(clean, "/signin");
  }

  /** Direct Google sign-in via Firebase popup. The Google account chooser
   * comes from Google itself — no fake suggestions. */
  async function signInGoogle() {
    setErr("");
    setLoading(true);
    try {
      const { user: fbUser, bqAccessToken } = await signInWithGoogle();
      const signedEmail = (fbUser.email ?? "").toLowerCase();

      // Enforce workspace domain allowlist (super-admins and invited members bypass all restrictions)
      if (tenantInfo && !isSuperAdminEmail(signedEmail)) {
        const isInvited = Array.isArray(tenantInfo.invitedMembers) &&
          tenantInfo.invitedMembers.includes(signedEmail);
        const emailDomain = signedEmail.split("@")[1] || "";
        const allowed = [tenantInfo.domain, ...(tenantInfo.allowedDomains || [])]
          .filter(Boolean)
          .map((d) => d!.toLowerCase());
        if (!isInvited && allowed.length > 0 && !allowed.includes(emailDomain)) {
          await signOut();
          setErr(`Access restricted: Workspace "${tenantInfo.name}" only permits accounts ending with @${allowed.join(", @")}.`);
          return;
        }
      }

      useAuthStore.getState().setFirebaseUser(fbUser.uid, fbUser.email, fbUser.displayName ?? null);
      if (bqAccessToken) {
        useAuthStore.getState().setBqToken(bqAccessToken, Date.now() + 55 * 60 * 1000);
      }
      onSignedIn?.();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  /** Dev-only manual sign-in (used when Firebase config isn't set locally). */
  async function manualSignIn(e: FormEvent) {
    e.preventDefault();
    setErr("");
    const target = {
      email: email.trim().toLowerCase(),
      name: name.trim() || (email.split("@")[0] ?? ""),
    };
    if (!target.email) return setErr("Enter your work email.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(target.email))
      return setErr("That doesn't look like a valid email.");
    if (!isAllowedEmail(target.email)) {
      return setErr("Enter a valid email address.");
    }
    setLoading(true);
    try {
      // Try Firebase popup first; if the project isn't configured (local dev), fall back.
      try {
        const { user: fbUser, bqAccessToken } = await signInWithGoogle();
        useAuthStore
          .getState()
          .setFirebaseUser(fbUser.uid, fbUser.email, fbUser.displayName ?? null);
        if (bqAccessToken) {
          useAuthStore.getState().setBqToken(bqAccessToken, Date.now() + 55 * 60 * 1000);
        }
      } catch {
        await new Promise((r) => setTimeout(r, 850));
        useAuthStore.getState().setFirebaseUser(`dev-${target.email}`, target.email, target.name);
      }
      onSignedIn?.();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-bg">
        <div className="auth-blob b1" />
        <div className="auth-blob b2" />
        <div className="auth-blob b3" />
        <div className="auth-blob b4" />
        <div className="auth-grid" />
      </div>

      <div className="auth-split-layout">
        {/* Left: Gong-style Executive Intelligence Showcase */}
        <div className="auth-showcase">
          <div className="auth-showcase-badge">
            <span className="auth-badge-dot" />
            <span>SellSynq — Your Invisible AI Sales Assistant</span>
          </div>

          <h2 className="auth-showcase-h2">
            Turn every customer conversation into <span className="auth-h2-gradient">closed revenue</span>.
          </h2>

          <p className="auth-showcase-sub">
            SellSynq listens live during enterprise sales calls, whispers real-time objection handling &
            battlecards, detects deal risks before they stall, and writes your CRM recap in seconds.
          </p>

          {/* Interactive Live Call Preview Widget */}
          <div className="auth-preview-card">
            <div className="auth-preview-top">
              <div className="auth-preview-live-tag">
                <span className="live-pulse" /> Live Call Active
              </div>
              <span className="auth-preview-client">Acme Enterprise · Cloud Migration</span>
              <span className="auth-preview-time">24:18</span>
            </div>

            <div className="auth-preview-metrics">
              <div className="auth-metric-item">
                <span className="metric-title">Talk vs Listen Ratio</span>
                <div className="auth-mini-bar">
                  <div className="bar-rep" style={{ width: "42%" }} />
                  <div className="bar-prospect" style={{ width: "58%" }} />
                </div>
                <div className="metric-caption">
                  <span style={{ color: "var(--primary)" }}>Rep 42%</span> · <span style={{ color: "#64748B" }}>Buyer 58%</span>
                  <span style={{ color: "#059669", marginLeft: 6, fontWeight: 600 }}>✓ Optimal Discovery</span>
                </div>
              </div>

              <div className="auth-metric-item">
                <span className="metric-title">Deal Momentum</span>
                <div className="auth-score-pill">
                  <span className="score-num">89</span>
                  <span className="score-denom">/100</span>
                  <span className="score-state">Strong Buy Signal</span>
                </div>
              </div>
            </div>

            <div className="auth-preview-whisper">
              <div className="whisper-head">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                <span>Live Battlecard Whisper</span>
              </div>
              <div className="whisper-body">
                Buyer raised concern over migration timeline. Reference <strong>FinTech Global Case Study</strong> where deployment completed in 14 days and saved $380,000 in Year 1.
              </div>
              <div className="whisper-tag">⚡ 87% win rate when using this counter-point</div>
            </div>
          </div>

          <div className="auth-showcase-stats">
            <div>
              <div className="stat-big">48%</div>
              <div className="stat-desc">Higher Win Rate</div>
            </div>
            <div>
              <div className="stat-big">3.2x</div>
              <div className="stat-desc">Faster Quota Ramp</div>
            </div>
            <div>
              <div className="stat-big">100%</div>
              <div className="stat-desc">Automated Recaps</div>
            </div>
          </div>
        </div>

        {/* Right: Modern Auth Card */}
        <div className="auth-shell">
          <div className="auth-brand">
            <div className="auth-logo">
              <svg width="22" height="22" viewBox="0 0 40 40" fill="none">
                <path
                  d="M12 15C12 12.2386 14.2386 10 17 10H24C26.7614 10 29 12.2386 29 15C29 17.7614 26.7614 20 24 20H16C13.2386 20 11 22.2386 11 25C11 27.7614 13.2386 30 16 30H23C25.7614 30 28 27.7614 28 25"
                  stroke="var(--primary)"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="27" cy="15" r="3" fill="var(--primary)" />
                <circle cx="13" cy="25" r="3" fill="#6366f1" />
              </svg>
            </div>
            <div>
              <div className="auth-brand-name">SellSynq</div>
              <div className="auth-brand-sub">Your Invisible AI Sales Assistant</div>
            </div>
          </div>

          <div className="auth-card">
            {currentSubdomain && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px 14px",
                  borderRadius: 12,
                  background: "rgba(99, 102, 241, 0.08)",
                  border: "1px solid rgba(99, 102, 241, 0.2)",
                  marginBottom: 16,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 8,
                      background: "var(--primary)",
                      color: "#fff",
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 15,
                    }}
                  >
                    {tenantInfo?.name ? tenantInfo.name.charAt(0).toUpperCase() : "🏢"}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-1)" }}>
                      {tenantInfo?.name || currentSubdomain} Workspace
                    </div>
                    <div style={{ fontSize: 11.5, color: "var(--text-3)", fontFamily: "var(--font-mono, monospace)" }}>
                      {currentSubdomain}.sellsynq.com
                    </div>
                  </div>
                </div>
                <a
                  href="/"
                  style={{ fontSize: 12, color: "var(--primary)", textDecoration: "none", fontWeight: 600 }}
                  title="Return to general SellSynq portal"
                >
                  All Hubs →
                </a>
              </div>
            )}
            <h1 className="auth-h1">
              {tenantInfo?.name
                ? `Sign in to ${tenantInfo.name}`
                : currentSubdomain
                ? `Sign in to ${currentSubdomain}`
                : "Sign in to SellSynq"}
            </h1>
            <p className="auth-sub">
              {tenantInfo?.name
                ? `Welcome to ${tenantInfo.name}'s revenue intelligence workspace with role-tailored coaching.`
                : "Access real-time call copilot, pipeline radar, and coaching intelligence."}
            </p>

            <button type="button" className="goog-btn" onClick={signInGoogle} disabled={loading}>
              <GoogleG />
              <span>{loading ? "Connecting securely…" : "Continue with Google"}</span>
            </button>

            {err && (
              <div className="auth-err">
                <Alert size={14} /> {err}
              </div>
            )}

            <div className="auth-divider">
              <span className="auth-divider-line" />
              <span className="auth-divider-text">or sign in with email</span>
              <span className="auth-divider-line" />
            </div>

            <form onSubmit={manualSignIn} className="auth-form">
              <label className="auth-label">
                Work Email
                <input
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </label>

              {showManual && (
                <label className="auth-label">
                  Full name <span className="auth-opt">(optional)</span>
                  <input
                    type="text"
                    placeholder="Your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </label>
              )}

              <button type="submit" className="pill-btn primary lg auth-submit-btn" disabled={loading}>
                {loading ? "Signing in…" : "Launch Copilot →"}
              </button>

              {!showManual && (
                <button
                  type="button"
                  className="ghost-btn xs"
                  onClick={() => setShowManual(true)}
                  style={{ alignSelf: "center", color: "var(--text-3)" }}
                >
                  + Add display name
                </button>
              )}
            </form>

            {!currentSubdomain && (
              <div
                style={{
                  marginTop: 18,
                  paddingTop: 16,
                  borderTop: "1px dashed var(--border-soft)",
                }}
              >
                {!showWorkspaceFinder ? (
                  <button
                    type="button"
                    onClick={() => setShowWorkspaceFinder(true)}
                    className="ghost-btn xs"
                    style={{ width: "100%", textAlign: "center", color: "var(--primary)", fontWeight: 600 }}
                  >
                    🏢 Sign in to your company workspace (.sellsynq.com)
                  </button>
                ) : (
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-2)", marginBottom: 6 }}>
                      Enter your company workspace subdomain:
                    </div>
                    <form onSubmit={handleGoToWorkspace} style={{ display: "flex", gap: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", position: "relative", flex: 1 }}>
                        <input
                          type="text"
                          placeholder="e.g. wideops"
                          value={subdomainInput}
                          onChange={(e) => setSubdomainInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                          style={{
                            width: "100%",
                            padding: "8px 10px",
                            paddingRight: 110,
                            borderRadius: 8,
                            border: "1px solid var(--border-soft)",
                            fontSize: 13,
                            fontFamily: "var(--font-mono, monospace)",
                          }}
                        />
                        <span
                          style={{
                            position: "absolute",
                            right: 8,
                            fontSize: 12,
                            color: "var(--text-3)",
                            pointerEvents: "none",
                            fontFamily: "var(--font-mono, monospace)",
                          }}
                        >
                          .sellsynq.com
                        </span>
                      </div>
                      <button
                        type="submit"
                        className="pill-btn primary sm"
                        disabled={!subdomainInput.trim()}
                        style={{ padding: "8px 14px", fontSize: 12 }}
                      >
                        Go →
                      </button>
                    </form>
                  </div>
                )}
              </div>
            )}

            <div className="auth-foot">
              <span className="mono">v2.4.0 Enterprise</span>
              <span>•</span>
              <button type="button" className="auth-foot-link" onClick={() => setPrivacyOpen(true)}>
                Privacy
              </button>
              <span>•</span>
              <button type="button" className="auth-foot-link" onClick={() => setTermsOpen(true)}>
                Terms
              </button>
              <span>•</span>
              <a href="mailto:support@sellsynq.com">Support</a>
            </div>
          </div>
        </div>
      </div>
      <PrivacyPolicyModal open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
      <TermsOfUseModal open={termsOpen} onClose={() => setTermsOpen(false)} />
    </div>
  );
}

function GoogleG({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden>
      <path
        fill="#4285F4"
        d="M44.5 20H24v8.5h11.8C34.7 33.9 30 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.3 0 6.3 1.2 8.6 3.2l6-6C34.6 4.5 29.6 2.5 24 2.5 12.1 2.5 2.5 12.1 2.5 24S12.1 45.5 24 45.5c12.4 0 21.5-9 21.5-21.5 0-1.4-.1-2.7-.5-4z"
      />
      <path
        fill="#34A853"
        d="M6.3 14.7l7 5.1C15.4 16 19.4 13 24 13c3.3 0 6.3 1.2 8.6 3.2l6-6C34.6 4.5 29.6 2.5 24 2.5c-8.4 0-15.6 4.7-19.3 11.6z"
      />
      <path
        fill="#FBBC05"
        d="M24 45.5c5.8 0 10.6-1.9 14.2-5.2l-6.6-5.4C29.6 36.4 27 37 24 37c-6 0-11-3.9-12.8-9.3l-7 5.4C7.6 41 15.2 45.5 24 45.5z"
      />
      <path
        fill="#EA4335"
        d="M44.5 20H24v8.5h11.8C35 30.9 33.5 33 31.6 34.4l6.6 5.4C42.6 36.6 45.5 31 45.5 24c0-1.4-.1-2.7-.5-4z"
      />
    </svg>
  );
}
